import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import supabase from '../../../config/supabaseClient';
import { 
  ArrowLeft, 
  MessageCircle, 
  Phone, 
  Calendar,
  User,
  Edit
} from 'lucide-react';
import toast from 'react-hot-toast';

const DetalhesUsuarioWhatsapp = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [usuario, setUsuario] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUsuario = async () => {
      try {
        const { data, error } = await supabase
          .from('usuario_whatsapp')
          .select('*')
          .eq('id', id)
          .single();

        if (error) throw error;
        setUsuario(data);
      } catch (error) {
        console.error('Erro ao carregar usuário:', error);
        toast.error('Erro ao carregar usuário');
      } finally {
        setLoading(false);
      }
    };

    loadUsuario();
  }, [id]);

  // Função para obter o nome do usuário (nome_completo ou nome_contato)
  const obterNomeUsuario = (usuario) => {
    return usuario.nome_completo || usuario.nome_contato || 'Nome não informado';
  };

  if (loading) {
    return (
      <div className="gestao-usuarios detalhes-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando detalhes do usuário...</p>
        </div>
      </div>
    );
  }

  if (!usuario) {
    return (
      <div className="gestao-usuarios detalhes-container">
        <div className="empty-state">
          <MessageCircle size={48} />
          <h3>Usuário não encontrado</h3>
          <p>O usuário solicitado não foi encontrado.</p>
          <Link to="/gestao-usuarios/populacao" className="button primary">
            Voltar à Lista
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="gestao-usuarios detalhes-container">
      <div className="detalhes-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <button 
            className="back-button"
            onClick={() => navigate('/gestao-usuarios/populacao')}
          >
            <ArrowLeft size={20} />
          </button>
          <div className="header-content">
            <h1>
              <MessageCircle size={32} />
              Detalhes do Usuário do WhatsApp
            </h1>
            <p>Informações completas do usuário {obterNomeUsuario(usuario)}</p>
          </div>
        </div>
      </div>

      <div className="detalhes-content">
        <div className="detalhes-grid">
          {/* Informações Básicas */}
          <div className="detalhes-section">
            <h3>Informações Básicas</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Nome Principal</label>
                <div className="info-value">
                  <User size={16} />
                  {obterNomeUsuario(usuario)}
                </div>
              </div>
              
              {usuario.nome_contato && (
                <div className="info-item">
                  <label>Nome do Contato</label>
                  <div className="info-value">
                    <User size={16} />
                    {usuario.nome_contato}
                  </div>
                </div>
              )}
              
              {usuario.nome_completo && (
                <div className="info-item">
                  <label>Nome Completo</label>
                  <div className="info-value">
                    <User size={16} />
                    {usuario.nome_completo}
                  </div>
                </div>
              )}
              
              {usuario.telefone && (
                <div className="info-item">
                  <label>Telefone</label>
                  <div className="info-value">
                    <Phone size={16} />
                    {usuario.telefone}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Status dos Dados */}
          <div className="detalhes-section">
            <h3>Status dos Dados</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Tipo de Nome</label>
                <div className="info-value">
                  {usuario.nome_completo ? (
                    <span className="status-badge status-ativo">
                      <User size={14} />
                      Nome Completo Disponível
                    </span>
                  ) : (
                    <span className="status-badge status-warning">
                      <User size={14} />
                      Apenas Nome do Contato
                    </span>
                  )}
                </div>
              </div>
              
              <div className="info-item">
                <label>Completude dos Dados</label>
                <div className="info-value">
                  {usuario.nome_completo && usuario.telefone ? (
                    <span className="status-badge status-ativo">Dados Completos</span>
                  ) : (
                    <span className="status-badge status-warning">Dados Parciais</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Informações de Sistema */}
          <div className="detalhes-section full-width">
            <h3>Informações do Sistema</h3>
            <div className="info-grid">
              <div className="info-item">
                <label>Cadastrado em</label>
                <div className="info-value">
                  <Calendar size={16} />
                  {new Date(usuario.created_at).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
              
              {usuario.updated_at && (
                <div className="info-item">
                  <label>Última atualização</label>
                  <div className="info-value">
                    <Calendar size={16} />
                    {new Date(usuario.updated_at).toLocaleDateString('pt-BR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </div>
                </div>
              )}
              
              <div className="info-item">
                <label>ID do Usuário</label>
                <div className="info-value">
                  <User size={16} />
                  {usuario.id}
                </div>
              </div>
            </div>
          </div>

          {/* Observações sobre os Dados */}
          <div className="detalhes-section full-width">
            <h3>Observações</h3>
            <div className="info-note">
              <p>
                <strong>Sobre os nomes:</strong> O sistema prioriza o nome completo quando disponível. 
                Caso contrário, utiliza o nome do contato salvo no WhatsApp.
              </p>
              {!usuario.nome_completo && (
                <p className="warning-note">
                  <strong>Atenção:</strong> Este usuário possui apenas o nome do contato. 
                  O nome completo não foi fornecido durante as interações.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetalhesUsuarioWhatsapp;